<script setup>
import { ref, computed, watch } from 'vue'
import { useEmailTemplatesStore } from '@/stores'

const props = defineProps({
  isDialogVisible: {
    type: Boolean,
    default: false,
  },
  template: {
    type: Object,
    default: null,
  },
  versions: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:isDialogVisible', 'rollback'])

const emailTemplatesStore = useEmailTemplatesStore()

// State
const loading = ref(false)
const rollingBack = ref(false)
const selectedVersion = ref(null)
const showDiffDialog = ref(false)
const showConfirmRollback = ref(false)

// Dialog visibility
const dialogVisible = computed({
  get: () => props.isDialogVisible,
  set: val => emit('update:isDialogVisible', val),
})

// Sort versions by version number descending
const sortedVersions = computed(() => {
  if (!props.versions) return []
  
  return [...props.versions].sort((a, b) => b.version - a.version)
})

// Format date
const formatDate = dateString => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// View version details
const viewVersion = version => {
  selectedVersion.value = version
  showDiffDialog.value = true
}

// Confirm rollback
const confirmRollback = version => {
  selectedVersion.value = version
  showConfirmRollback.value = true
}

// Perform rollback
const performRollback = async () => {
  if (!selectedVersion.value || !props.template?._id) return

  rollingBack.value = true
  try {
    await emailTemplatesStore.rollbackToVersion(
      props.template._id,
      selectedVersion.value.version,
    )
    emit('rollback')
    showConfirmRollback.value = false
    dialogVisible.value = false
  } catch (error) {
    console.error('Error rolling back:', error)
  } finally {
    rollingBack.value = false
  }
}

// Close dialog
const closeDialog = () => {
  dialogVisible.value = false
}

// Get truncated content preview
const getContentPreview = content => {
  if (!content) return 'No content'

  // Strip HTML tags and truncate
  const text = content.replace(/<[^>]*>/g, '').trim()
  if (text.length > 100) {
    return text.substring(0, 100) + '...'
  }
  
  return text || 'Empty content'
}
</script>

<template>
  <VDialog
    v-model="dialogVisible"
    max-width="900"
    scrollable
  >
    <VCard v-if="template">
      <!-- Header -->
      <VCardTitle class="d-flex align-center pa-4">
        <VIcon class="mr-2">
          mdi-history
        </VIcon>
        <span>Version History</span>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          @click="closeDialog"
        />
      </VCardTitle>

      <VDivider />

      <!-- Template Info -->
      <div class="pa-4 bg-grey-lighten-4">
        <h3 class="text-h6">
          {{ template.displayName }}
        </h3>
        <p class="text-caption text-grey mb-0">
          Template: {{ template.templateName }} | Current Version: v{{ template.version }}
        </p>
      </div>

      <VDivider />

      <!-- Version List -->
      <VCardText
        class="pa-0"
        style="max-height: 500px; overflow-y: auto;"
      >
        <VList v-if="sortedVersions.length > 0">
          <template
            v-for="(version, index) in sortedVersions"
            :key="version._id"
          >
            <VListItem class="version-item">
              <template #prepend>
                <div class="version-badge">
                  <VChip
                    :color="version.version === template.version ? 'primary' : 'grey'"
                    size="small"
                  >
                    v{{ version.version }}
                  </VChip>
                  <VChip
                    v-if="version.version === template.version"
                    color="success"
                    size="x-small"
                    class="ml-1"
                  >
                    Current
                  </VChip>
                </div>
              </template>

              <VListItemTitle class="d-flex align-center">
                <span>{{ formatDate(version.createdAt) }}</span>
              </VListItemTitle>

              <VListItemSubtitle>
                <span
                  v-if="version.changeNotes"
                  class="change-notes"
                >
                  {{ version.changeNotes }}
                </span>
                <span
                  v-else
                  class="text-grey"
                >
                  No change notes
                </span>
              </VListItemSubtitle>

              <VListItemSubtitle class="text-caption mt-1">
                <span v-if="version.modifiedBy">
                  Modified by: {{ version.modifiedBy }}
                </span>
              </VListItemSubtitle>

              <template #append>
                <div class="version-actions">
                  <VBtn
                    size="small"
                    variant="text"
                    @click="viewVersion(version)"
                  >
                    <VIcon size="small">
                      mdi-eye
                    </VIcon>
                    <span class="ml-1">View</span>
                  </VBtn>

                  <VBtn
                    v-if="version.version !== template.version"
                    size="small"
                    variant="text"
                    color="warning"
                    @click="confirmRollback(version)"
                  >
                    <VIcon size="small">
                      mdi-restore
                    </VIcon>
                    <span class="ml-1">Rollback</span>
                  </VBtn>
                </div>
              </template>
            </VListItem>

            <VDivider v-if="index < sortedVersions.length - 1" />
          </template>
        </VList>

        <div
          v-else
          class="text-center pa-8"
        >
          <VIcon
            size="64"
            color="grey-lighten-1"
          >
            mdi-history
          </VIcon>
          <p class="text-grey mt-4">
            No version history available
          </p>
        </div>
      </VCardText>

      <VDivider />

      <!-- Actions -->
      <VCardActions class="pa-4">
        <VSpacer />
        <VBtn
          variant="text"
          @click="closeDialog"
        >
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- View Version Dialog -->
  <VDialog
    v-model="showDiffDialog"
    max-width="1000"
    scrollable
  >
    <VCard v-if="selectedVersion">
      <VCardTitle class="d-flex align-center">
        <VIcon class="mr-2">
          mdi-file-document
        </VIcon>
        Version {{ selectedVersion.version }} Details
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          @click="showDiffDialog = false"
        />
      </VCardTitle>

      <VDivider />

      <VCardText>
        <VRow class="mb-4">
          <VCol
            cols="12"
            md="6"
          >
            <p class="text-caption text-grey mb-1">
              Created At
            </p>
            <p class="mb-0">
              {{ formatDate(selectedVersion.createdAt) }}
            </p>
          </VCol>
          <VCol
            cols="12"
            md="6"
          >
            <p class="text-caption text-grey mb-1">
              Modified By
            </p>
            <p class="mb-0">
              {{ selectedVersion.modifiedBy || 'Unknown' }}
            </p>
          </VCol>
        </VRow>

        <VRow
          v-if="selectedVersion.subject"
          class="mb-4"
        >
          <VCol cols="12">
            <p class="text-caption text-grey mb-1">
              Subject
            </p>
            <p class="mb-0">
              {{ selectedVersion.subject }}
            </p>
          </VCol>
        </VRow>

        <VRow
          v-if="selectedVersion.changeNotes"
          class="mb-4"
        >
          <VCol cols="12">
            <p class="text-caption text-grey mb-1">
              Change Notes
            </p>
            <p class="mb-0">
              {{ selectedVersion.changeNotes }}
            </p>
          </VCol>
        </VRow>

        <div class="mb-4">
          <p class="text-caption text-grey mb-2">
            Content Preview
          </p>
          <div class="content-preview pa-4">
            <iframe
              :srcdoc="selectedVersion.content"
              class="preview-iframe"
              sandbox="allow-same-origin"
              frameborder="0"
            />
          </div>
        </div>

        <VExpansionPanels>
          <VExpansionPanel title="View HTML Source">
            <VExpansionPanelText>
              <pre class="html-source">{{ selectedVersion.content }}</pre>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCardText>

      <VDivider />

      <VCardActions class="pa-4">
        <VBtn
          v-if="selectedVersion.version !== template?.version"
          variant="outlined"
          color="warning"
          @click="confirmRollback(selectedVersion)"
        >
          <VIcon start>
            mdi-restore
          </VIcon>
          Rollback to This Version
        </VBtn>
        <VSpacer />
        <VBtn
          variant="text"
          @click="showDiffDialog = false"
        >
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Confirm Rollback Dialog -->
  <VDialog
    v-model="showConfirmRollback"
    max-width="500"
  >
    <VCard>
      <VCardTitle class="d-flex align-center">
        <VIcon
          class="mr-2"
          color="warning"
        >
          mdi-alert
        </VIcon>
        Confirm Rollback
      </VCardTitle>

      <VCardText>
        <p>
          Are you sure you want to rollback to
          <strong>version {{ selectedVersion?.version }}</strong>?
        </p>
        <p class="text-caption text-grey mb-0">
          This will create a new version with the content from version {{ selectedVersion?.version }}.
          The current version will be preserved in history.
        </p>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn
          variant="text"
          :disabled="rollingBack"
          @click="showConfirmRollback = false"
        >
          Cancel
        </VBtn>
        <VBtn
          color="warning"
          :loading="rollingBack"
          @click="performRollback"
        >
          <VIcon start>
            mdi-restore
          </VIcon>
          Rollback
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.version-item {
  padding: 16px;
}

.version-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}

.change-notes {
  color: var(--ds-text-strong);
}

.version-actions {
  display: flex;
  gap: 8px;
}

.content-preview {
  border: 1px solid var(--ds-border-default);
  border-radius: 8px;
  background-color: var(--ds-surface-card);
  overflow: hidden;
}

.preview-iframe {
  width: 100%;
  height: 400px;
  border: none;
}

.html-source {
  background-color: var(--ds-surface-subtle);
  padding: 16px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 11px;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
}
</style>
